import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApiResponse, ApiResult, ApiVariables, ApiRequestOptions } from '../../../api/apiSchema';
import { AsyncResult, Result } from '../../../core';
import { BindingProps, StoreNode } from '../../../store';
import { IChartDataSource } from '../chartSchema';
import { Store } from '../../../store/store';
import { error, Error } from '../../../core/error';
import { parseKeenJsonResult } from '../utils/parseKeenJsonResult';
import { ReactionChartMode, ReactionData, ReactionsChartParams, ReactionSeriesData } from './reactionsChartSchema';
import { InputPlayerReactionName, InputPlayerReactionNames } from '../../playerReactions/playerReactionSchema';
import { ApiError } from '../../../api/apiError';
import { DateTime } from 'luxon';
import { Datum } from '../xySeriesChart';
import { JobSourceType, JobStatus } from '@clipr/lib';

type Props = BindingProps<{
  params: ReactionsChartParams,
  suppressFetchOnParamsChange?: boolean
}>

export class ReactionsDataSource
  extends StoreNode
  implements IChartDataSource<ReactionSeriesData> {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this);

    this.onPropChange('params',
      this.handleParamsChange);
  }

  // #region Props
  @computed get params(): ReactionsChartParams | null {
    return this.getResolvedProp('params') ?? null;
  }
  set params(val: ReactionsChartParams | null) {
    this.setProp('params', val);
  }

  @computed get suppressFetchOnParamsChange(): boolean | null {
    return this.getResolvedProp('suppressFetchOnParamsChange') ?? null;
  }
  set suppressFetchOnParamsChange(val: boolean | null) {
    this.setProp('suppressFetchOnParamsChange', val);
  }
  // #endregion

  // #region Handlers
  private handleParamsChange = () => {
    // this.fetch();
  }
  // #endregion

  @computed get mode(): ReactionChartMode {
    return this.params?.mode ?? ReactionChartMode.ReactionType;
  }

  readonly data = observable.array<ReactionSeriesData>([], { deep: false });

  private abortController: AbortController | null = null;

  @observable isFetching = false;
  @observable error: Error | null = null;

  @action
  reset() {
    this.abort();

    this.isFetching = false;
    this.error = null;
    this.abortController = null;

    this.data.clear();
  }

  @action
  async fetch(): AsyncResult {

    this.reset();

    this.isFetching = true;

    const abortCtrl = new AbortController();
    this.abortController = abortCtrl;

    const { params } = this;
    if (!params)
      return [null];

    const startDateArg = params.startDate?.toISO();
    const endDateArg = params.endDate?.toISO();
    const timezoneArg = params.timezone ?? undefined;
    const teamIdArg = params.teamId ?? undefined;

    if (!startDateArg || !endDateArg)
      return [null, error('Unknown', 'Invalid parameters')];

    const reqArgs: ApiVariables<'keenBatch'> = {
      args: {
        start: startDateArg,
        end: endDateArg,
        timezone: timezoneArg,
        teamId: teamIdArg
      }
    };

    const api = this.store.api;
    const reqOpts: ApiRequestOptions = {
      signal: abortCtrl.signal
    };

    let reqPromise: ApiResult<'keenBatch'>;

    switch (params.mode) {
      default:
      case ReactionChartMode.ReactionType:
        reqPromise = api.runQuery('keen_getReactions', reqArgs, reqOpts);
        break;

      case ReactionChartMode.JobSource:
        reqPromise = api.runQuery('keen_getReactionsByJobSource', reqArgs, reqOpts);
        break;
    }

    if (!reqPromise) {
      console.error('Mode not implemented');
      return this.handleError(error('Unknown', `Invalid mode ${params.mode} was provided to ReactionsDataSource`));
    }

    const [res, err] = await reqPromise;
    if (err)
      return this.handleError(err);

    const [data, insertErr] = this.insert(res!);
    if (insertErr)
      return this.handleError(insertErr);

    runInAction(() => {
      this.isFetching = false;
    });

    return [data!];
  }

  cancel(): void {
    this.abort();
  }

  @observable isBatchRequesting = false;

  notifyBatchRequestStarted() {
    this.isBatchRequesting = true;
  }
  notifyBatchRequestCompleted() {
    this.isBatchRequesting = false;
  }

  @action
  insert(res: ApiResponse<'keenBatch' | 'keen_getReactions' | 'keen_getReactionsByJobSource'>): Result<ReactionSeriesData[]> {
    let dataRes: Result<ReactionSeriesData[]>;

    switch (this.mode) {
      default:
      case ReactionChartMode.ReactionType:
        dataRes = this.getDataByReactionType(res);
        break;
      case ReactionChartMode.JobSource:
        dataRes = this.getDataByJobSource(res);
        break;
    }

    const [data, err] = dataRes;

    if (err)
      return this.handleError(err);

    this.data.replace(data ?? []);
    return [data ?? []];
  }

  private abort() {

    const lastAbortCtrl = this.abortController;
    if (lastAbortCtrl)
      lastAbortCtrl.abort();
    this.abortController = null;
  }

  @action
  private handleError(err?: Error | string): Result {
    if (!err)
      err = new Error();
    if (typeof err === 'string')
      err = new Error('Unknown', err);

    if (err instanceof ApiError && err.type === 'RequestAborted') {
      // for aborted requests we only return the error but we don't set it on the instance and we do not repor it as error
      // console.warn(`Request aborted in ReactionsDataSource`);
      return [null, err];

    } else {
      console.error(`An error occured in ReactionsDataSource: `, err);

      this.isFetching = false;
      this.abort();
      this.error = err;

      return [null, err];
    }
  }

  @action
  private handleApiResponse(datum: Datum, reactionName?: string): ReactionData {
    let reactionData: { value?: number; result?: number } | undefined;

    switch (this.mode) {
      case ReactionChartMode.ReactionType:
        reactionData = (datum?.value as unknown as { reactionName: string; result: number }[])?.find(
          (value) =>
            value.reactionName === reactionName
        )
        break;
      case ReactionChartMode.JobSource:
        reactionData = datum;
        break;
    }

    return {
      // TODO: format the label according to use case (date or hours/minutes)
      date: DateTime
        .fromISO(datum?.timeframe?.start ?? '')
        .setLocale('en-US')
        .toLocaleString(DateTime.DATE_SHORT),
      value: (reactionData?.value || reactionData?.result) ?? 0
    }
  }

  @action
  handleApiData(data: Datum[]): ReactionSeriesData[] {
    return InputPlayerReactionNames.map((reactionName: InputPlayerReactionName): ReactionSeriesData => (
      {
        dataKey: reactionName,
        data: data?.map((datum) =>
          this.handleApiResponse(datum, reactionName)) ?? [],
      })
    );
  }

  @action
  private getDataByReactionType(data: ApiResponse<'keenBatch' | 'keen_getReactions'>): Result<ReactionSeriesData[]> {

    const apiResultStr = data.keenBatch.reactions?.result;
    const [apiResult, parseErr] = parseKeenJsonResult<Datum[]>(apiResultStr);

    if (parseErr)
      return [null, parseErr];

    const res = InputPlayerReactionNames.map((reactionName: InputPlayerReactionName): ReactionSeriesData => (
      {
        dataKey: reactionName,
        data: apiResult?.map((datum) =>
          this.handleApiResponse(datum, reactionName)) ?? [],
      })
    );

    return [res];
  }

  @action
  private getDataByJobSource(data: ApiResponse<'keenBatch' | 'keen_getReactionsByJobSource'>): Result<ReactionSeriesData[]> {

    const apiResultStr = data.keenBatch.reactionsByJobSource?.result;
    const [apiResult, parseErr] = parseKeenJsonResult<Datum[]>(apiResultStr);

    if (parseErr)
      return [null, parseErr];

    const totalReactions: Datum[] | undefined = apiResult?.map((series: Datum): Datum => {
      let value = 0;
      if (Array.isArray(series.value)) {
        value = series.value.reduce((prev, curr) => prev + curr.result, 0);
      }
      return {
        value,
        timeframe: series.timeframe
      }
    })
    const staticVideoReactions: Datum[] | undefined = apiResult?.map((series: Datum): Datum => {
      let value = 0;
      if (Array.isArray(series.value)) {
        const results = series.value
          .filter(job => job['job.source'] !== JobSourceType.Live)
          .map(job => job.result);
        value = results.reduce((prev, curr) => prev + curr, 0) || 0;
      }
      return {
        timeframe: series.timeframe,
        value
      }
    });
    const liveVideoReactions: Datum[] | undefined = apiResult?.map((series: Datum): Datum => {
      let value = 0;
      if (Array.isArray(series.value)) {
        value = series.value.find(job =>
          job['job.source'] === JobSourceType.Live && job['job.status'] === JobStatus.LiveStreaming)?.result || 0;
      }
      return {
        timeframe: series.timeframe,
        value
      }
    });
    const postLiveVideoReactions: Datum[] | undefined = apiResult?.map((series: Datum): Datum => {
      let value = 0;
      if (Array.isArray(series.value)) {
        const results = series.value
          .filter(job =>
            job['job.source'] === JobSourceType.Live && job['job.status'] !== JobStatus.LiveStreaming)
          .map(job => job.result);
        value = results.reduce((prev, curr) => prev + curr, 0) || 0;
      }
      return {
        timeframe: series.timeframe,
        value
      }
    });

    const output = this.handleDataByJobSourceResponse({
      totalReactions,
      staticVideoReactions,
      liveVideoReactions,
      postLiveVideoReactions
    });

    return [output];
  }

  @action
  private handleDataByJobSourceResponse({
    totalReactions,
    staticVideoReactions,
    liveVideoReactions,
    postLiveVideoReactions
  }: {
    totalReactions?: Datum[];
    staticVideoReactions?: Datum[];
    liveVideoReactions?: Datum[];
    postLiveVideoReactions?: Datum[];
  }): ReactionSeriesData[] {
    return [
      {
        dataKey: 'Total',
        data:
          totalReactions?.map((datum) => this.handleApiResponse(datum)) ??
          [],
      },
      {
        dataKey: 'Uploaded Videos',
        data:
          staticVideoReactions?.map((datum) => this.handleApiResponse(datum)) ??
          [],
      },
      {
        dataKey: 'Live Event',
        data:
          liveVideoReactions?.map((datum) => this.handleApiResponse(datum)) ??
          [],
      },
      {
        dataKey: 'Post Live Event',
        data:
          postLiveVideoReactions?.map((datum) => this.handleApiResponse(datum)) ??
          [],
      }
    ]
  }
}
